/*
  A very small, deliberately limited text formatter for blog bodies.

  Why not a Markdown library: this content is rendered on a storefront
  that handles customer data, and the usual shortcut — parsing to HTML
  and using dangerouslySetInnerHTML — turns the admin panel into a
  stored-XSS vector the moment an admin account is compromised. This
  renderer only ever produces React elements with text children, so
  markup in a post is escaped by React and can never execute.

  Supported, and nothing else:

    blank line       new paragraph
    ## Heading       section heading
    ### Heading      smaller heading
    - item           bullet list
    > quote          pull quote
    **bold**         bold
    [text](https://) link, http(s) only
*/

const INLINE = /(\*\*[^*\n]+\*\*|\[[^\]\n]+\]\([^)\s]+\))/g;

/* Only ever produce http(s) links. Blocks javascript: and data: URLs,
   which are the classic way to smuggle script into a link. */
const safeHref = (url) => (/^https?:\/\//i.test(url) ? url : null);

function inline(text, keyPrefix) {
  return String(text)
    .split(INLINE)
    .filter((part) => part !== '' && part !== undefined)
    .map((part, i) => {
      const key = `${keyPrefix}-${i}`;

      if (/^\*\*[^*\n]+\*\*$/.test(part)) {
        return <strong key={key}>{part.slice(2, -2)}</strong>;
      }

      const link = part.match(/^\[([^\]\n]+)\]\(([^)\s]+)\)$/);
      if (link) {
        const href = safeHref(link[2]);
        // An unsafe URL degrades to plain text rather than vanishing.
        return href ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {link[1]}
          </a>
        ) : (
          <span key={key}>{link[1]}</span>
        );
      }

      return part; // plain string — React escapes it
    });
}

export default function RichText({ text, className = 'rich-text' }) {
  if (!text) return null;

  const blocks = String(text)
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);

  return (
    <div className={className}>
      {blocks.map((block, i) => {
        const key = `b${i}`;

        if (block.startsWith('### ')) {
          return <h3 key={key}>{inline(block.slice(4), key)}</h3>;
        }
        if (block.startsWith('## ')) {
          return <h2 key={key}>{inline(block.slice(3), key)}</h2>;
        }

        if (block.startsWith('> ')) {
          const quote = block
            .split('\n')
            .map((l) => l.replace(/^>\s?/, ''))
            .join(' ');
          return <blockquote key={key}>{inline(quote, key)}</blockquote>;
        }

        // A block where every line is a bullet becomes a list.
        const lines = block.split('\n');
        if (lines.every((l) => /^[-*]\s+/.test(l))) {
          return (
            <ul key={key}>
              {lines.map((l, j) => (
                <li key={`${key}-${j}`}>
                  {inline(l.replace(/^[-*]\s+/, ''), `${key}-${j}`)}
                </li>
              ))}
            </ul>
          );
        }

        // Single newlines inside a paragraph stay as line breaks.
        return (
          <p key={key}>
            {lines.map((l, j) => (
              <span key={`${key}-${j}`}>
                {inline(l, `${key}-${j}`)}
                {j < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
